import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faCheck, faTimes, faClock, faInfoCircle, faCheckDouble } from "@fortawesome/free-solid-svg-icons";
import React, { useState } from "react";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch } from "react-redux";
import moment from "moment";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import SubtleBadge from "components/common/SubtleBadge";
import { ViewSinglePayment } from "./payment/config/PaymentApi";
import ClaimViewSinglePayment from "./payment/components/ClaimViewSinglePayment";
import { useAppContext } from "providers/AppProvider";

const ClaimPaymentTableCard = ({ data }) => {
    const dispatch = useDispatch();
    const {
        config: { isDark }
    } = useAppContext();
    const [singlePaymentData, setSinglePaymentData] = useState([]);
    const [show, setShow] = useState(false);

    const handleViewSinglePayment = async (paymentNo) => {
        dispatch(ViewSinglePayment({
            paymentno: paymentNo || '',
        })).unwrap().then((response) => {
            if (response?.success) {
                setShow(true);
                setSinglePaymentData(response?.data);
            }
        });
    };

    const columns = [
        {
            accessorKey: "index",
            header: "#",
            cell: ({ row: { index } }) => (
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle bg-200 d-flex flex-center fs-11"
                        style={{ width: "25px", height: "25px" }}
                    >
                        {index + 1}
                    </div>
                </div>
            ),
        },
        {
            accessorKey: "paymentno",
            header: "Payment No.",
            cell: ({ row: { original, index } }) => (
                <OverlayTrigger
                    placement="bottom"
                    overlay={
                        <Tooltip
                            id={`tooltip-payment-${index}`}
                            style={{
                                "--bs-tooltip-bg": "transparent",
                                "--bs-tooltip-padding-y": "0",
                                "--bs-tooltip-padding-x": "0",
                            }}
                        >
                            <div className={isDark ? "bg-dark" : "bg-white"}
                                style={{
                                    padding: "12px 16px",
                                    textAlign: "left",
                                    borderRadius: "12px",
                                    backdropFilter: "blur(10px)",
                                    border: isDark ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid rgba(0, 0, 0, 0.1)",
                                    boxShadow: isDark ? "0 20px 40px rgba(0,0,0,0.4)" : "0 10px 30px rgba(0,0,0,0.1)",
                                    width: "300px",
                                    minWidth: "280px",
                                    maxWidth: "600px",
                                    zoom: "75%"
                                }}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <span
                                        style={{
                                            fontSize: "0.8rem",
                                            fontWeight: "800",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.1rem",
                                            color: isDark ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
                                        }}
                                    >
                                        view payment details
                                    </span>
                                </div>
                            </div>
                        </Tooltip>
                    }
                >
                    <div
                        className="d-flex align-items-center w-100"
                        style={{
                            cursor: "pointer",
                            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                        onClick={() => handleViewSinglePayment(original?.paymentno)}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-1px)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                        }}
                    >
                        <div className="flex-center fs-11 text-primary fw-semi-bold">
                            {original?.paymentno || ''}
                        </div>
                    </div>
                </OverlayTrigger>
            ),
        },
        {
            accessorKey: "claim_date",
            header: "Payment Date",
            cell: ({ row: { original } }) => (
                <div className="d-flex align-items-center w-100">
                    <div className="flex-center cursor-pointer fs-11">
                        {original?.paymentdate ? moment(original?.paymentdate).format("DD-MM-YYYY") : '' || ''}
                    </div>
                </div>
            ),
        },
        {
            accessorKey: "amount",
            header: "Amount",
            cell: ({ row: { original } }) => (
                <div className="fs-11 fw-bold">{original?.amount || "0.00"}</div>
            ),
        },
        {
            accessorKey: "payment",
            header: "Payment Fee",
            cell: ({ row: { original } }) => (
                <div className="fs-11">{original?.payment || "0.00"}</div>
            ),
        },
        {
            accessorKey: "earlyfee",
            header: "Early Fee",
            cell: ({ row: { original } }) => (
                <div className="fs-11">{original?.earlyfee || "0.00"}</div>
            ),
        },
        {
            accessorKey: "nphiesfee",
            header: "Nphies Fee",
            cell: ({ row: { original } }) => (
                <div className="fs-11">{original?.nphiesfee || "0.00"}</div>
            ),
        },
        {
            accessorKey: "paymenttype",
            header: "Payment Type",
            cell: ({ row: { original } }) => (
                <div className="fs-11 text-capitalize">{original?.paymenttype || "-"}</div>
            ),
        },
        {
            accessorKey: "pay_status",
            header: "Status",
            cell: ({ row: { original } }) => {
                const value = original.pay_status?.toLowerCase();
                const statusConfig = {
                    paid: { bg: "#d9f8eb", color: "#00894f", icon: faCheck },
                    verified: { bg: "#d9e7fa", color: "#215cac", icon: faCheckDouble },
                    pending: { bg: "#fefad2", color: "#a7970d", icon: faClock },
                    failed: { bg: "#fbdbe1", color: "#b82c46", icon: faTimes },
                };

                const config = statusConfig[value] || {
                    bg: "#f8f9fa",
                    color: "#5d6776",
                    icon: faInfoCircle,
                };

                return (
                    <SubtleBadge
                        pill
                        style={{
                            backgroundColor: config.bg,
                            color: config.color,
                            border: `1px solid ${config.color}20`,
                            width: "100px",
                        }}
                        className="d-flex flex-center fs-11 p-2 gap-1"
                    >
                        <FontAwesomeIcon icon={config.icon} transform="shrink-2" className="ms-1 me-1" />
                        {original.pay_status || "-"}
                    </SubtleBadge>
                );
            },
        },
    ];

    const table = useAdvanceTable({
        data: data || [],
        columns,
        manualPagination: false,
    });

    return (
        <>
            <Card className="shadow-sm mt-3">
                <Card.Header className="bg-primary text-white p-2 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faLink} className="me-2" />
                        <h6 className="mb-0 text-white fs-11 fw-bold">Payment Details</h6>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    {data?.length > 0 ? (
                        <AdvanceTableProvider {...table}>
                            <AdvanceTable
                                tableProps={{
                                    size: "sm",
                                    className: "fs-10 mb-0 overflow-hidden",
                                    striped: true,
                                    hover: true,
                                }}
                                headerClassName="bg-200 text-nowrap align-middle"
                                rowClassName="align-middle white-space-nowrap"
                            />
                        </AdvanceTableProvider>
                    ) : (
                        <div className="text-center p-3 text-muted fs-11">No Payment Details Available</div>
                    )}
                </Card.Body>
            </Card>
            <ClaimViewSinglePayment
                show={show}
                setShow={setShow}
                data={singlePaymentData}
                fromPage={true}
            />
        </>
    );
};

export default ClaimPaymentTableCard;
